import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const BurnRateIndicator = () => {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [burnData, setBurnData] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [selectedMonths, setSelectedMonths] = useState(12);
    const infoRef = useRef(null);
    const buttonRef = useRef(null);

    const periodOptions = [
        { value: 3, label: '3 månader' },
        { value: 6, label: '6 månader' },
        { value: 12, label: '12 månader' },
        { value: 24, label: '24 månader' }
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    useEffect(() => {
        fetchBurnRate();
    }, [companyId, selectedMonths]);

    // Close info popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                infoRef.current &&
                !infoRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setShowInfo(false);
            }
        };

        if (showInfo) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showInfo]);

    const fetchBurnRate = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(getApiUrl(`${companyId}/dashboard/burn-rate?months=${selectedMonths}`));
            if (response.status === 200) {
                setBurnData(response.data.burnRate);
            }
        } catch (error) {
            console.error('Error fetching burn rate:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <span style={{ color: 'var(--secondary)', fontSize: '0.875rem' }}>Laddar...</span>
            </div>
        );
    }

    if (!burnData) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--secondary)' }}>
                Ingen data tillgänglig
            </div>
        );
    }

    // Use CSS variable values for theme consistency
    const categoryConfig = {
        critical: { color: 'var(--danger, #f72b50)', bgColor: 'rgba(247, 43, 80, 0.1)', label: 'Kritisk', desc: '< 3 månader' },
        warning: { color: 'var(--warning, #FFAA2B)', bgColor: 'rgba(255, 170, 43, 0.1)', label: 'Varning', desc: '3-6 månader' },
        healthy: { color: 'var(--primary, #44814E)', bgColor: 'var(--rgba-primary-1, rgba(68, 129, 78, 0.1))', label: 'Bra', desc: '6-12 månader' },
        strong: { color: 'var(--info, #D653C1)', bgColor: 'rgba(214, 83, 193, 0.1)', label: 'Stark', desc: '> 12 månader' },
        unknown: { color: 'var(--secondary, #717579)', bgColor: 'var(--body-bg, #F8F8F8)', label: 'Okänd', desc: 'Kan ej beräknas' }
    };

    const config = categoryConfig[burnData.runwayCategory] || categoryConfig.unknown;

    // Gauge percentage (cap at 24 months = 100%)
    const gaugePercent = burnData.runway !== null
        ? Math.min(100, (burnData.runway / 24) * 100)
        : 100;

    return (
        <div>
            {/* Info Popover */}
            {showInfo && (
                <div
                    ref={infoRef}
                    className="dashboard-info-popover"
                    style={{
                        position: 'absolute',
                        top: '60px',
                        right: '16px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        border: '1px solid var(--border, #d7dae3)',
                        padding: '16px',
                        width: '300px',
                        zIndex: 1000
                    }}
                >
                    <div style={{
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: 'var(--heading, #333)',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid var(--border, #d7dae3)'
                    }}>
                        Om Kassareserv & Löptid
                    </div>

                    <div style={{ fontSize: '0.8rem', color: 'var(--secondary)', lineHeight: 1.6, marginBottom: '12px' }}>
                        Visar hur många månader företagets kassa räcker baserat på de senaste {selectedMonths} månadernas genomsnittliga nettokostnad.
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '6px'
                        }}>
                            Beräkning
                        </div>
                        <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--secondary)',
                            backgroundColor: 'var(--body-bg, #F8F8F8)',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--border, #d7dae3)'
                        }}>
                            <div style={{ marginBottom: '4px' }}>
                                <strong>Nettokostnad</strong> = Snittutgifter - Snittintäkter
                            </div>
                            <div>
                                <strong>Löptid</strong> = Total kassa ÷ Nettokostnad/mån
                            </div>
                        </div>
                    </div>

                    <div>
                        <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '6px'
                        }}>
                            Statusnivåer
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {Object.entries(categoryConfig).filter(([key]) => key !== 'unknown').map(([key, cfg]) => (
                                <div key={key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '0.75rem'
                                }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: cfg.color
                                    }} />
                                    <span style={{ color: cfg.color, fontWeight: 600, minWidth: '50px' }}>
                                        {cfg.label}
                                    </span>
                                    <span style={{ color: 'var(--secondary)' }}>
                                        {cfg.desc}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Top controls: Period selector and Info button */}
            <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="dashboard-dropdown dropdown-toggle"
                    >
                        {selectedMonths} mån
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ fontSize: '0.8rem' }}>
                        {periodOptions.map(option => (
                            <Dropdown.Item
                                key={option.value}
                                active={selectedMonths === option.value}
                                onClick={() => setSelectedMonths(option.value)}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>

                <button
                    ref={buttonRef}
                    onClick={() => setShowInfo(!showInfo)}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        opacity: showInfo ? 1 : 0.5,
                        transition: 'opacity 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = showInfo ? 1 : 0.5}
                    title="Visa förklaring"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--secondary)' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="8" r="1" fill="currentColor" />
                    </svg>
                </button>
            </div>

            {/* Subtitle */}
            <div style={{
                textAlign: 'center',
                fontSize: '0.75rem',
                color: 'var(--secondary)',
                marginBottom: '1rem'
            }}>
                Hur länge räcker kassan?
            </div>

            {/* Gauge visualization */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{
                    position: 'relative',
                    width: '140px',
                    height: '70px',
                    margin: '0 auto',
                    overflow: 'hidden'
                }}>
                    {/* Background arc */}
                    <div style={{
                        position: 'absolute',
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        border: '12px solid var(--border, #d7dae3)',
                        borderBottomColor: 'transparent',
                        borderRightColor: 'transparent',
                        transform: 'rotate(-45deg)',
                        top: 0,
                        left: 0
                    }} />
                    {/* Filled arc */}
                    <div style={{
                        position: 'absolute',
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        border: '12px solid transparent',
                        borderTopColor: config.color,
                        borderLeftColor: gaugePercent > 50 ? config.color : 'transparent',
                        transform: `rotate(${-45 + (gaugePercent * 1.8)}deg)`,
                        top: 0,
                        left: 0,
                        transition: 'transform 1s ease'
                    }} />
                    {/* Center text */}
                    <div style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: config.color,
                            lineHeight: 1
                        }}>
                            {burnData.runway !== null ? `${burnData.runway}` : '\u221E'}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--secondary)' }}>
                            månader
                        </div>
                    </div>
                </div>

                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: config.color,
                    backgroundColor: config.bgColor,
                    marginTop: '0.5rem'
                }}>
                    {config.label}
                </div>
            </div>

            {/* Stats grid */}
            <div className="dashboard-status-grid">
                <div className="dashboard-status-item">
                    <div className="dashboard-status-label">Nettokostnad/mån</div>
                    <div className="dashboard-status-value">
                        {formatCurrency(burnData.monthly)}
                    </div>
                </div>
                <div className="dashboard-status-item">
                    <div className="dashboard-status-label">Total kassa</div>
                    <div className="dashboard-status-value">
                        {formatCurrency(burnData.totalCash)}
                    </div>
                </div>
                <div className="dashboard-status-item">
                    <div className="dashboard-status-label">Totala utgifter/mån</div>
                    <div className="dashboard-status-value dashboard-status-value--danger">
                        {formatCurrency(burnData.gross)}
                    </div>
                </div>
                <div className="dashboard-status-item">
                    <div className="dashboard-status-label">Snittintäkt/mån</div>
                    <div className="dashboard-status-value dashboard-status-value--primary">
                        {formatCurrency(burnData.avgRevenue)}
                    </div>
                </div>
            </div>

            {/* Data source note */}
            <div style={{
                marginTop: '0.75rem',
                fontSize: '0.65rem',
                color: 'var(--secondary)',
                textAlign: 'center',
                fontStyle: 'italic'
            }}>
                Baserat på senaste {selectedMonths} månaders data
            </div>
        </div>
    );
};

export default BurnRateIndicator;
